import gsap from 'gsap';
import { SplitText } from 'gsap/SplitText';

import AnimationController from '../../assets/js/controllers/animation';
import DetectController from '../../assets/js/controllers/detect';
import ResizeController from '../../assets/js/controllers/resize';
import ScrollController from '../../assets/js/controllers/scroll';

import App from '../../assets/js/app';

import { map, getRootOffset } from '../../assets/js/helpers';

gsap.registerPlugin(SplitText);

export default class Work {

	animation;

	isDesktop;

	el = {};
	bounds = {};

	splitHeadings = [];

	min;
	max;

	previous = 0;
	current = 0;

	static selector = 'work';

	constructor(container) {
		this.el = {
			container,
			heading: container.querySelector(`.${this.constructor.selector} .h1`),
			items: container.querySelectorAll(`.${this.constructor.selector}__item`),
			sidebar: container.querySelector(`.${this.constructor.selector}__sidebar`),
			sidebarItems: container.querySelectorAll(`.${this.constructor.selector}__sidebar-item`)
		};

		this.isDesktop = DetectController.isDesktop;

		if (this.isDesktop) {
			this.measure();
			this.start();
			this.addEventListeners();

			for (const sidebarItem of this.el.sidebarItems) {
				const heading = sidebarItem.querySelector('h3');
				const split = new SplitText(heading, { type: 'lines' });
				this.splitHeadings.push(new SplitText(split.lines, { type: 'lines' }));
			}
		}
	}

	addEventListeners() {
		App.on('preloadComplete', this.#onPreloadComplete);
		ResizeController.add(this.#onWindowResize);
	}

	removeEventListeners() {
		App.off('preloadComplete', this.#onPreloadComplete);
		ResizeController.remove(this.#onWindowResize);
	}

	#onPreloadComplete = () => {
		this.measure();
	}

	#onWindowResize = () => {
		this.measure();
	}

	measure() {
		const headingBounds = getRootOffset(this.el.heading);
		const containerBounds = getRootOffset(this.el.container);

		this.bounds = {
			headingTop: headingBounds.top,
			containerTop: containerBounds.top,
			headingHeight: this.el.container.offsetHeight,
			containerHeight: this.el.container.offsetHeight,
			lastSidebarHeight: this.el.sidebarItems[this.el.sidebarItems.length - 1].offsetHeight,
			winHeight: window.innerHeight,
			items: []
		};

		for (const item of this.el.items) {
			const bounds = getRootOffset(item);
			this.bounds.items.push({
				top: bounds.top,
				height: item.offsetHeight,
				update: false
			});
		}

		this.el.sidebar.style.height = `${this.bounds.lastSidebarHeight}px`;

		this.max = this.bounds.winHeight;
		this.min = -1 * this.max;
	}

	calculateParallax() {
		const scroll = this.bounds.headingTop - this.bounds.winHeight - ScrollController.eased;
		const from = this.bounds.winHeight;
		const to = -1 * this.bounds.headingHeight;
		return map(scroll, from, to, this.min, this.max);
	}

	start() {
		this.animation = AnimationController.set({
			update: this.#update,
			render: this.#render
		});
	}

	stop() {
		AnimationController.clear(this.animation);
		this.animation = null;
	}

	#update = () => {
		const scroll = ScrollController.eased;

		if (scroll > this.bounds.containerTop - this.bounds.winHeight
			&& scroll < this.bounds.containerTop + this.bounds.containerHeight) {
			this.current = this.calculateParallax();
			return true;
		}
	}

	#render = () => {
		const scroll = ScrollController.eased + this.bounds.winHeight;

		for (let i = 0; i < this.bounds.items.length; i++) {
			const bounds = this.bounds.items[i];
			const sidebarItem = this.el.sidebarItems[i];

			if (scroll >= bounds.top + (i !== 0 ? this.bounds.winHeight : 0)
				&& scroll < bounds.top + bounds.height + this.bounds.winHeight) {
				if (!bounds.update) {
					bounds.update = true;
					sidebarItem.classList.add('is-active');
				}
			} else {
				if (bounds.update) {
					bounds.update = false;
					sidebarItem.classList.remove('is-active');
				}
			}
		}

		this.el.heading.style.transform = `translate3d(${this.current}px, 0, 0)`;
	}

	destroy() {
		this.removeEventListeners();
	}

}
